/**
 * The dark theme — the same system, reflected.
 *
 * Not a second palette. Every value is DERIVED from `palette`, the same way
 * `surfaces.ts` derives the light ramp, so there is still exactly one list of
 * brand colours and two mappings of it. The typed record at the bottom is keyed
 * by `DesignToken`: add a light token and forget its dark twin and this file
 * stops compiling, rather than the dark theme silently inheriting a light value.
 *
 * ── The inversion, stated once ──────────────────────────────────────────────
 * The light system is "ink on glacier paper". The dark one is the same sentence
 * read the other way round: glacier on ink.
 *
 *   - The PAGE is ink, deepened. Same hue as `palette.ink` (~265°), lower
 *     lightness, so the dark page is recognisably the brand's own navy and not a
 *     neutral grey that happens to be dark.
 *   - The INK (primary text) is the light page colour, `palette.page`. The two
 *     themes literally trade their two anchor colours.
 *   - The CTA stays "the ink": in light it is the darkest thing on the page, so
 *     in dark it is the lightest — a glacier pill with a navy label. The rule
 *     from design-language.md §1 survives intact: the action is never the accent.
 *   - The ACCENT uses `palette.accentOnDark`, which already exists for exactly
 *     this job (the brand mark on its navy tile) and is pinned by logo.test.ts.
 *
 * ── Why the ladder still climbs ─────────────────────────────────────────────
 * Elevation reads the same way in both themes — what is closer to you is
 * lighter — so `paper` and `raised` step UP from the page here too, and `hover`
 * still presses DOWN into it. What changes is the step: near black the eye
 * resolves far less lightness difference, so a step that reads on paper
 * (0.022 L) vanishes on navy. Dark steps are ~2x.
 */
import { adjustLightness, mixOver, oklchToRgb, rgbToHex, rgbToOklch, type Rgb } from './color';
import { palette } from './palette';
import { seat as seatLight, status as statusLight, type DesignToken } from './surfaces';

/** One perceptual step on the dark ladder. */
const STEP = 0.042;

/** The page: ink's own hue, taken down to where four surfaces fit above it. */
const INK = rgbToOklch(palette.ink);
const PAGE_L = 0.19;
const PAGE_C = INK.c * 0.8;

function lift(steps: number): string {
  return rgbToHex(oklchToRgb({ l: PAGE_L + STEP * steps, c: PAGE_C, h: INK.h }));
}

const page = lift(0);

export const surfacesDark = {
  hover: lift(-0.6),
  page,
  paper: lift(1),
  raised: lift(2),
} as const;

/** Primary text is the light theme's page. */
const ink = palette.page;

export const linesDark = {
  hairline: mixOver(ink, page, 0.13),
  hairlineStrong: mixOver(ink, page, 0.24),
} as const;

export const textDark = {
  ink,
  /** Same construction as the light tier: halfway to the faint tier. */
  inkDim: mixOver(ink, page, 0.8),
  /** The floor. Still clears AA on `raised`, the brightest dark surface. */
  inkFaint: mixOver(ink, page, 0.62),
} as const;

export const accentDark = {
  /** Graphics. Brighter than the light accent so a 2px marker still separates
   *  from a navy ground; the hue is untouched. */
  accent: adjustLightness(palette.accent, 0.14),
  /** Text — the brand's accent-on-dark, already AA-pinned on `brandTile`. */
  accentInk: palette.accentOnDark,
  accentBright: adjustLightness(palette.accentOnDark, 0.05),
  accentSoft: mixOver(palette.accentOnDark, page, 0.14),
  accentLine: mixOver(palette.accentOnDark, page, 0.36),
} as const;

export const buttonDark = {
  /** The CTA is the lightest thing on the page, as it was the darkest before. */
  button: palette.page,
  buttonLabel: palette.button,
  /** A light fill cannot brighten much further, so hover steps DOWN a little. */
  buttonHover: adjustLightness(palette.page, -0.06),
} as const;

/** Status hues lifted until they clear 4.5:1 on navy; hue held, so a readout
 *  that goes ok → warn → danger means the same thing in both themes. */
export const statusDark = {
  danger: adjustLightness(statusLight.danger, 0.27),
  warn: adjustLightness(statusLight.warn, 0.28),
  ok: adjustLightness(statusLight.ok, 0.28),
} as const;

/** The listener. Lifted less than `danger` so the two stay a lightness step
 *  apart — the same separation surfaces.ts insists on in light — but far
 *  enough to clear AA on `raised`, because the legend labels it. */
export const seatDark = adjustLightness(seatLight, 0.28);

/**
 * The full dark set. `Record<DesignToken, string>` is the completeness gate —
 * see the registry-completeness rule in AGENTS.md.
 */
/** Near-black in ink's hue: a shadow must stay darker than the ground. On a
 *  navy page it carries less of the elevation than it does on paper — the
 *  surface ladder (raised is lighter) does most of that work, as it should
 *  in a dark UI. */
export const shadowDark = rgbToHex(oklchToRgb({ l: 0.08, c: PAGE_C, h: INK.h }));

export const designTokensDark: Record<DesignToken, string> = {
  ...surfacesDark,
  ...linesDark,
  ...textDark,
  ...accentDark,
  ...buttonDark,
  ...statusDark,
  seat: seatDark,
  shadow: shadowDark,
};

export type ThemeName = 'light' | 'dark';

/**
 * Re-ink a colour authored for paper so it reads on navy: lightness reflected,
 * hue held, chroma eased slightly (a light tint cannot carry the chroma a dark
 * one does without looking neon). This is the same move the whole theme makes —
 * ink ↔ page — applied to a single colour.
 *
 * For BAKED light assets only — the generated field's line LUT, where the
 * colours live in a texture rather than in tokens. Anything that has a token
 * uses the token's dark value instead; do not route UI colour through this.
 */
export function reflectForDark(rgb: Rgb): Rgb {
  const { l, c, h } = rgbToOklch(rgb);
  return oklchToRgb({ l: Math.min(0.94, Math.max(0.5, 1.12 - l)), c: c * 0.85, h });
}
